import {
    Fragment,
    useEffect,
    useState
} from "react";
import { useSelector } from "react-redux";
import {
    useNavigate,
    useParams,
    useSearchParams
} from "react-router-dom";

import { BookOpenIcon, Cog6ToothIcon } from "@heroicons/react/24/outline";

import {
    selectEnv,
    selectIsSidebarLarge,
    selectMemberships,
    selectUser
} from "../lib/scraper.slice";
import { BackendObj } from "../lib/backend";
import {
    IItemSlim,
    IViewExtractJob
} from "../lib/backend/extractions.types.generated";
import {
    classNames,
    prettyTime,
    redirectToExternalPageWithPostData,
    setDocumentTitle
} from "../lib/utils";
import { USER_ROLES } from "../lib/consts";

import { ExtractJobEvents } from "../components/ExtractJobs";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ItemBreadcrumb } from "../components/ItemBreadcrumb";
import { ItemList } from "./Items";
import { Button } from "../components/Button";
import { ExtractButton } from "../components/ExtractButton";
import { SupportLink } from "../components/SupportLink";
import { ButtonMenu, IButtonMenuItem } from "../components/ButtonMenu";
import { TwoRowHeader } from "../components/Header";

export function JobDetail() {
    const navigate = useNavigate();
    const { job_uuid } = useParams<{ job_uuid: string | undefined }>();
    const [search_params] = useSearchParams();
    const item_uuid = search_params.get("item_uuid");

    const env = useSelector(selectEnv);
    const is_sidebar_large = useSelector(selectIsSidebarLarge);
    const user = useSelector(selectUser);
    const memberships = useSelector(selectMemberships);

    const is_admin = user.role === USER_ROLES.admin;

    const [job, setJob] = useState<IViewExtractJob | undefined>(undefined);
    const [last_refresh_ts, setLastRefreshTs] = useState<number>(Date.now());
    const [ticker, setTicker] = useState<number>(0);
    const [is_job_running, setIsJobRunning] = useState<boolean>(false);
    const [show_job_completed_message, setShowJobCompletedMessage] = useState<boolean>(false);
    const [is_events_open, setIsEventsOpen] = useState<boolean>(false);

    useEffect(() => {
        if (job_uuid !== undefined) {
            BackendObj.extractions.getJobDetails({ job_uuid })
                .then(({ job }) => {
                    setJob(job);
                    setLastRefreshTs(Date.now());

                    const job_still_running = job?.status === "pending" || job?.status === "running";
                    if (job_still_running && ticker < 720) { // 720 = 12 minutes
                        setIsJobRunning(true);
                        setTimeout(() => { setTicker(ticker + 1); }, 1000);
                    } else {
                        if (is_job_running === true) {
                            setShowJobCompletedMessage(true);
                            setTimeout(() => { setShowJobCompletedMessage(false); }, 5000);
                        }
                        setIsJobRunning(false);
                    }
                });
        } else {
            setJob(undefined);
        }
    }, [job_uuid, ticker]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (job !== undefined) {
            setDocumentTitle(`Job - ${job.title.length > 0 ? job.title : "[no subject]"}`, env);
        } else {
            setDocumentTitle("Job - loading...", env);
        }
    }, [env, job]);

    const onItemSelected = (item: IItemSlim, event?: React.MouseEvent) => {
        if (event?.metaKey || event?.ctrlKey) {
            window.open(`/item/${item.uuid}?job_uuid=${job_uuid}`, "_blank");
        } else {
            navigate(`/item/${item.uuid}?job_uuid=${job_uuid}`);
        }
    };

    if (job_uuid === undefined || job === undefined) {
        return <div className={classNames("hidden lg:fixed lg:right-0 lg:inset-y-0 lg:flex lg:flex-row", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
            <LoadingSpinner />
        </div>;
    }

    const navigateBack = () => {
        if (item_uuid) {
            navigate(`/item/${item_uuid}`);
        } else {
            navigate("/items");
        }
    }

    const downloadItems = (type: "excel" | "json") => {
        const url = `/api/items/${type}`;
        const post_data = {
            item_uuids: items.map(item => item.uuid)
        };
        redirectToExternalPageWithPostData(url, true, post_data);
    }

    const download_buttons: IButtonMenuItem[] = [
        { title: "Excel", onClick: () => downloadItems("excel") },
        { title: "JSON", onClick: () => downloadItems("json") },
    ];

    const { details, items } = job;
    const job_org = memberships.find(m => m.org.uuid === job.org_uuid);

    return <div className={classNames("flex-row lg:fixed lg:right-0 lg:inset-y-0 overflow-y-auto", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
        <TwoRowHeader
            title={job.title.length > 0 ? job.title : "[no subject]"}
            subtitle={details.template_uuid !== undefined ? <ItemBreadcrumb
                template_uuid={details.template_uuid}
                template_name={details.template_name ?? ""}
                endpoint_uuid={details.endpoint_uuid}
                endpoint_name={details.endpoint_name}
            /> : <Fragment />}
            org_name={job_org?.org.name}
            buttons={<Fragment>
                <ButtonMenu title="Download" items={download_buttons} />
                {is_admin && <Button icon={Cog6ToothIcon} href={`/admin/prompt-log/${job.uuid}`} />}
                {<Button icon={BookOpenIcon} onClick={() => setIsEventsOpen(true)} />}
                <ExtractButton org_uuid={job.org_uuid} template_uuid={details.template_uuid} endpoint_uuid={details.endpoint_uuid} />
            </Fragment>}
            tooltip={`Last update: ${last_refresh_ts ? prettyTime(last_refresh_ts) : "/"}`}
            onBack={navigateBack}
        />

        <div className="max-w-5xl pl-2 pr-2">
            <div className="px-4 max-w-5xl">
                {job.status === "running" && <div className="mt-4 p-4 border bg-candy_corn-50 rounded text-sm text-gray-900">
                    <div className="flex flex-col gap-y-2">
                        <div>Job is still being processed.</div>
                        <div>Extracted items will appear below. You can follow the progress in the "Event Log" section (book icon).</div>
                    </div>
                </div>}
                {job.status === "error" && <div className="mt-4 p-4 border bg-torch_red-50 rounded text-sm text-gray-900">
                    <div className="flex flex-col gap-y-2">
                        <p>There was an error processing the job.</p>
                        <p>You can see the error message in the "Event Log" section (book icon).</p>
                        <p>Please try again or contact <SupportLink />.</p>
                    </div>
                </div>}
                {show_job_completed_message && job.status !== "error" && <div className="mt-4 p-4 border bg-mint-50 rounded text-sm text-gray-900">
                    <div className="flex flex-col gap-y-2">
                        <p>Job completed.</p>
                        <p>Extracted items are listed below.</p>
                    </div>
                </div>}
            </div>
        </div>

        <div className="max-w-5xl p-6">
            <div className="px-4 py-4 space-y-4">
                <Fragment>
                    {items.length > 0 && <ItemList items={items} onItemSelected={onItemSelected} show_template={true} is_endpoint={false} />}
                    {items.length === 0 && <div className="text-gray-400 text-xs">No results yet.</div>}
                </Fragment>
            </div>
        </div>

        <ExtractJobEvents
            user_uuid={job.user_uuid}
            org_uuid={job.org_uuid}
            job_uuid={job_uuid}
            open={is_events_open}
            type={is_admin ? "admin" : "org"}
            setOpen={setIsEventsOpen} />
    </div >;
}
